// ═══════════════════════════════════════════════════════
// useRealtimeSessions — Supabase Realtime + polling fallback
// Diffuse les changements de sessions à tous les écrans ouverts
// ═══════════════════════════════════════════════════════

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase.js';

const POLLING_INTERVAL = 15000; // 15s fallback si Realtime échoue

/**
 * Hook qui écoute les changements sur elixir_prep_sessions
 * et appelle onUpdate avec les données modifiées.
 *
 * @param {string} key - Clé d'activation (truthy = activé)
 * @param {function} onSessionChange - (type, session) => void
 * @param {function} onFullReload - () => void — refresh complet si nécessaire
 * @param {boolean} enabled - activer/désactiver
 */
export default function useRealtimeSessions(key, onSessionChange, onFullReload, enabled = true) {
  const channelRef = useRef(null);
  const pollingRef = useRef(null);
  const realtimeActive = useRef(false);
  const lastUpdate = useRef(Date.now());

  // ── Supabase Realtime ──
  useEffect(() => {
    if (!enabled || !key) return;

    const channel = supabase
      .channel('sessions-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'elixir_prep_sessions',
        },
        (payload) => {
          realtimeActive.current = true;
          lastUpdate.current = Date.now();

          if (payload.eventType === 'INSERT') {
            onSessionChange('INSERT', payload.new);
          } else if (payload.eventType === 'UPDATE') {
            onSessionChange('UPDATE', payload.new);
          } else if (payload.eventType === 'DELETE') {
            onSessionChange('DELETE', payload.old);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          realtimeActive.current = true;
          console.log('[realtime] Connecté — sessions');
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          realtimeActive.current = false;
          console.warn('[realtime] Déconnecté:', status);
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [key, enabled]);

  // ── Polling fallback — seulement si Realtime ne marche pas ──
  useEffect(() => {
    if (!enabled || !key) return;

    function poll() {
      // Si Realtime actif et récent, skip le polling
      if (realtimeActive.current && Date.now() - lastUpdate.current < 60000) return;

      // Polling uniquement si l'onglet est visible
      if (document.hidden) return;

      console.log('[realtime] Polling fallback...');
      onFullReload();
    }

    pollingRef.current = setInterval(poll, POLLING_INTERVAL);

    // Aussi recharger quand l'onglet redevient visible après > 30s
    function handleVisibility() {
      if (!document.hidden && Date.now() - lastUpdate.current > 30000) {
        lastUpdate.current = Date.now();
        onFullReload();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [key, enabled]);
}
